import React, { useState, useEffect } from 'react';
import {
  FaExclamationTriangle,
  FaShieldAlt,
  FaInfoCircle,
  FaCheckCircle,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaSync,
  FaPhoneAlt,
  FaBuilding,
} from 'react-icons/fa';
import { getAdvisories, dismissAdvisory, refreshAdvisories } from '../api/client';

const SEVERITY_CONFIG = {
  critical: {
    bg: 'linear-gradient(135deg, #fef2f2, #fecaca, #fef2f2)',
    border: '#ef4444',
    text: '#991b1b',
    badge: '#ef4444',
    icon: FaExclamationTriangle,
    label: 'CRITICAL',
    flash: true,
  },
  warning: {
    bg: 'linear-gradient(135deg, #fff7ed, #fed7aa, #fff7ed)',
    border: '#f59e0b',
    text: '#92400e',
    badge: '#f59e0b',
    icon: FaShieldAlt,
    label: 'WARNING',
    flash: true,
  },
  info: {
    bg: 'linear-gradient(135deg, #eff6ff, #bfdbfe, #eff6ff)',
    border: '#3b82f6',
    text: '#1e40af',
    badge: '#3b82f6',
    icon: FaInfoCircle,
    label: 'INFO',
    flash: false,
  },
  safe: {
    bg: 'linear-gradient(135deg, #f0fdf4, #bbf7d0, #f0fdf4)',
    border: '#22c55e',
    text: '#166534',
    badge: '#22c55e',
    icon: FaCheckCircle,
    label: 'SAFE',
    flash: false,
  },
};

const CATEGORY_LABELS = {
  health: 'Health',
  safety: 'Safety',
  political: 'Political',
  weather: 'Weather',
  entry_requirements: 'Entry Requirements',
  general: 'General',
};

const TravelAdvisoryBanner = ({ requestId }) => {
  const [data, setData] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (!requestId) return;
    fetchAdvisories();
  }, [requestId]);

  const fetchAdvisories = async () => {
    try {
      const result = await getAdvisories(requestId);
      setData(result);
    } catch (err) {
      // Silently fail - advisory banner is non-critical
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (advisoryId) => {
    try {
      await dismissAdvisory(requestId, advisoryId);
      setData(prev => ({
        ...prev,
        advisories: prev.advisories.filter(a => a.id !== advisoryId),
        total: prev.total - 1,
      }));
    } catch (err) {
      console.error('Failed to dismiss advisory:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await refreshAdvisories(requestId);
      setData(result);
      setExpandedIds(new Set());
    } catch (err) {
      console.error('Failed to refresh advisories:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading || !data || data.total === 0) return null;

  const { advisories, severity_counts, destination } = data;
  const hasCritical = (severity_counts?.critical || 0) > 0;
  const hasWarning = (severity_counts?.warning || 0) > 0;

  return (
    <div className="travel-advisory-banner mb-5">
      {/* Summary header — click to collapse/expand */}
      <div
        onClick={() => setCollapsed(prev => !prev)}
        style={{
          background: hasCritical
            ? 'linear-gradient(135deg, #dc2626, #991b1b)'
            : hasWarning
            ? 'linear-gradient(135deg, #f59e0b, #d97706)'
            : 'linear-gradient(135deg, #3b82f6, #2563eb)',
          borderRadius: collapsed ? '12px' : '12px 12px 0 0',
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'border-radius 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className={hasCritical || hasWarning ? 'advisory-flash-icon' : ''}>
            <FaShieldAlt style={{ color: 'white', fontSize: '18px' }} />
          </div>
          <div>
            <span style={{ color: 'white', fontWeight: 700, fontSize: '14px' }}>
              Travel Advisories{destination ? ` — ${destination}` : ''}
            </span>
            <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
              {Object.entries(severity_counts || {}).map(([sev, count]) =>
                count > 0 ? (
                  <span
                    key={sev}
                    style={{
                      background:
                        sev === 'critical'
                          ? '#dc2626'
                          : sev === 'warning'
                          ? '#f59e0b'
                          : sev === 'info'
                          ? '#3b82f6'
                          : 'rgba(255,255,255,0.25)',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '1px 8px',
                      borderRadius: '10px',
                    }}
                  >
                    {count} {sev}
                  </span>
                ) : null
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
            }}
            disabled={refreshing}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 12px',
              color: 'white',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <FaSync className={refreshing ? 'animate-spin' : ''} style={{ fontSize: '11px' }} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          {collapsed ? (
            <FaChevronDown style={{ color: 'white', fontSize: '13px' }} />
          ) : (
            <FaChevronUp style={{ color: 'white', fontSize: '13px' }} />
          )}
        </div>
      </div>

      {/* Advisory cards — collapsible */}
      {!collapsed && (
      <div
        style={{
          background: '#ffffff',
          borderRadius: '0 0 12px 12px',
          border: `1px solid ${hasCritical ? '#fca5a5' : hasWarning ? '#fcd34d' : '#93c5fd'}`,
          borderTop: 'none',
          overflow: 'hidden',
        }}
      >
        {advisories.map((advisory) => {
          const config = SEVERITY_CONFIG[advisory.severity] || SEVERITY_CONFIG.info;
          const IconComponent = config.icon;
          const isExpanded = expandedIds.has(advisory.id);

          return (
            <div
              key={advisory.id}
              className={config.flash ? 'advisory-flash-card' : ''}
              style={{
                borderBottom: '1px solid #f3f4f6',
                position: 'relative',
              }}
            >
              {/* Advisory header row */}
              <div
                onClick={() => toggleExpand(advisory.id)}
                style={{
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  background: config.flash ? undefined : 'transparent',
                }}
              >
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: config.badge + '18',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconComponent style={{ color: config.badge, fontSize: '13px' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span
                      style={{
                        background: config.badge,
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: '3px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {config.label}
                    </span>
                    <span style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600 }}>
                      {CATEGORY_LABELS[advisory.category] || advisory.category}
                    </span>
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                    {advisory.title}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                  {isExpanded ? (
                    <FaChevronUp style={{ color: '#9ca3af', fontSize: '11px' }} />
                  ) : (
                    <FaChevronDown style={{ color: '#9ca3af', fontSize: '11px' }} />
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismiss(advisory.id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                    }}
                    title="Dismiss"
                  >
                    <FaTimes style={{ color: '#d1d5db', fontSize: '11px' }} />
                  </button>
                </div>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{ padding: '0 16px 14px 54px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#4b5563', lineHeight: '1.6' }}>
                    {advisory.description}
                  </p>

                  {advisory.precautions && advisory.precautions.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 700, color: '#374151' }}>
                        Precautions:
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '16px' }}>
                        {advisory.precautions.map((p, i) => (
                          <li key={i} style={{ fontSize: '12px', color: '#4b5563', marginBottom: '2px' }}>
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {advisory.emergency_contacts && advisory.emergency_contacts.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaPhoneAlt style={{ fontSize: '10px' }} /> Emergency Contacts:
                      </p>
                      {advisory.emergency_contacts.map((c, i) => (
                        <p key={i} style={{ margin: '0 0 2px 16px', fontSize: '12px', color: '#4b5563' }}>
                          <strong>{c.name}:</strong> {c.number} {c.notes && <span style={{ color: '#9ca3af' }}>({c.notes})</span>}
                        </p>
                      ))}
                    </div>
                  )}

                  {advisory.consulate_info && advisory.consulate_info.name && (
                    <div>
                      <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaBuilding style={{ fontSize: '10px' }} /> Consulate:
                      </p>
                      <p style={{ margin: '0 0 2px 16px', fontSize: '12px', color: '#4b5563' }}>
                        {advisory.consulate_info.name}
                      </p>
                      {advisory.consulate_info.address && (
                        <p style={{ margin: '0 0 2px 16px', fontSize: '12px', color: '#6b7280' }}>
                          {advisory.consulate_info.address}
                        </p>
                      )}
                      {advisory.consulate_info.phone && (
                        <p style={{ margin: '0 0 2px 16px', fontSize: '12px', color: '#6b7280' }}>
                          Tel: {advisory.consulate_info.phone}
                        </p>
                      )}
                    </div>
                  )}

                  {advisory.source && (
                    <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#9ca3af' }}>
                      Source:{' '}
                      {advisory.source_url ? (
                        <a
                          href={advisory.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6', textDecoration: 'underline' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {advisory.source}
                        </a>
                      ) : (
                        advisory.source
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Inline CSS animations */}
      <style>{`
        .advisory-flash-icon {
          animation: advisoryPulse 2s ease-in-out infinite;
        }
        @keyframes advisoryPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.92); }
        }
        .advisory-flash-card {
          animation: advisoryGlow 3s ease-in-out infinite;
        }
        @keyframes advisoryGlow {
          0%, 100% { background-color: transparent; }
          50% { background-color: rgba(239, 68, 68, 0.04); }
        }
      `}</style>
    </div>
  );
};

export default TravelAdvisoryBanner;
